import { Box, IconButton } from '@atgfr8/atg-ui-tailwind';
import { CloseIcon, DeleteIcon, ArrowExpandIcon } from '@atgfr8/icons';
import * as pdfjsLib from 'pdfjs-dist';
import { createEffect, createSignal, Show } from 'solid-js';

type PdfPageProps = {
  page: pdfjsLib.PDFPageProxy;
  width: number;
  height: number;
  expandIconPosition?: 'top-right' | 'bottom-left';
  onDelete?: () => void;
  showDelete?: boolean;
};

const PdfPageOverlay = (props: PdfPageProps & { onClose: () => void }) => {
  let canvasRef: HTMLCanvasElement | undefined;

  const renderPage = async () => {
    if (!canvasRef) return;

    const outputScale = window.devicePixelRatio || 1;
    const scale = 1;

    const viewport = props.page.getViewport({ scale });
    const scaledViewport = props.page.getViewport({
      scale: scale * outputScale,
    });

    canvasRef.width = scaledViewport.width;
    canvasRef.height = scaledViewport.height;
    canvasRef.style.width = `${viewport.width}px`;
    canvasRef.style.height = `${viewport.height}px`;

    const context = canvasRef.getContext('2d');
    if (context) {
      context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
      await props.page.render({ canvasContext: context, viewport }).promise;
    }
  };

  createEffect(renderPage);

  return (
    <Box
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: '#fff',
        'z-index': 200,
        'box-shadow': '0 0 20px rgba(0,0,0,0.3)',
        'border-radius': '4px',
        overflow: 'auto',
        padding: '12px',
        'max-height': '95vh',
        'max-width': '95vw',
      }}
    >
      <canvas ref={(el) => (canvasRef = el)} />
      <IconButton
        onClick={props.onClose}
        dataTestId="pdf-page-overlay-close-button"
        class="absolute top-2 right-2 bg-black text-white hover:bg-[#222]"
      >
        <CloseIcon size={'16px'} />
      </IconButton>
    </Box>
  );
};

export const PdfPage = (props: PdfPageProps) => {
  let canvasRef: HTMLCanvasElement | undefined;
  const [showExpand, setShowExpand] = createSignal(false);
  const [showOverlay, setShowOverlay] = createSignal(false);

  const renderPage = async () => {
    if (!canvasRef) return;

    const outputScale = window.devicePixelRatio || 1;
    const scale = props.width / props.page.getViewport({ scale: 1 }).width;

    const viewport = props.page.getViewport({ scale });
    const scaledViewport = props.page.getViewport({
      scale: scale * outputScale,
    });

    canvasRef.width = scaledViewport.width;
    canvasRef.height = scaledViewport.height;

    canvasRef.style.width = `${viewport.width}px`;
    canvasRef.style.height = `${viewport.height}px`;

    const context = canvasRef.getContext('2d');
    if (context) {
      context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
      await props.page.render({ canvasContext: context, viewport }).promise;
    }
  };

  createEffect(renderPage);

  return (
    <>
      <Box
        style={{
          width: `${props.width}px`,
          height: `${props.height}px`,
          position: 'relative',
          overflow: 'hidden',
        }}
        class="[&:hover>.expand-icon]:opacity-100 [&:hover>.expand-icon]:pointer-events-auto"
        onMouseEnter={() => {
          if (props.expandIconPosition !== 'bottom-left') setShowExpand(true);
        }}
        onMouseLeave={() => {
          if (props.expandIconPosition !== 'bottom-left') setShowExpand(false);
        }}
      >
        <canvas
          ref={(el) => (canvasRef = el)}
          class="block mx-auto w-full! h-full!"
        />

        {(props.expandIconPosition === 'bottom-left' || showExpand()) && (
          <IconButton
            on:click={(e) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              e.stopPropagation();
              setShowOverlay(true);
            }}
            dataTestId="pdf-page-expand-button"
            class="expand-icon [&:hover]:bg-black [&:hover]:text-white"
            style={{
              position: 'absolute',
              top: props.expandIconPosition === 'bottom-left' ? 'auto' : '8px',
              bottom:
                props.expandIconPosition === 'bottom-left' ? '8px' : 'auto',
              left: props.expandIconPosition === 'bottom-left' ? '8px' : 'auto',
              right:
                props.expandIconPosition === 'bottom-left' ? 'auto' : '8px',
              'background-color': '#000',
              color: '#fff',
              'box-shadow': '0 2px 5px rgba(0,0,0,0.4)',
              opacity: props.expandIconPosition === 'bottom-left' ? 1 : 0,
              'pointer-events':
                props.expandIconPosition === 'bottom-left' ? 'auto' : 'none',
              transition: 'opacity 0.3s ease',
            }}
          >
            <ArrowExpandIcon size={'16px'} />
          </IconButton>
        )}

        {props.expandIconPosition === 'bottom-left' &&
          props.showDelete !== false && (
            <IconButton
              onClick={() => props.onDelete?.()}
              dataTestId="pdf-page-delete-button"
              class="[&:hover]:bg-[#b71c1c] [&:hover]:text-white"
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                'background-color': '#d32f2f',
                color: '#fff',
                'box-shadow': '0 2px 5px rgba(0,0,0,0.4)',
                transition: 'opacity 0.3s ease',
              }}
            >
              <DeleteIcon size={'16px'} />
            </IconButton>
          )}
      </Box>

      <Show when={showOverlay()}>
        <PdfPageOverlay {...props} onClose={() => setShowOverlay(false)} />
      </Show>
    </>
  );
};
